import React from 'react';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import axios from 'axios';

import DropdownComp from '../dropdown/dropdown';
import GoalServices from '../../services/goal.services';
import ErrorDisplay from "../common/errorDisplay.component";
import KrDataSourceIcon from "../krDataSourceIcon";
import AlignmentSlider from './alignmentSlider';
import { withTranslation } from 'react-i18next';
import ConfidenceFlag from "../common/confidenceFlag.component";
import { ALIGNMENT_SEARCH_ITEM_TYPE } from '../../constants/okr/alignmentFilter.constants';

const sortTree = (arr) => {
  arr?.sort((a, b) => {
    if (a.teamName === b.teamName) {
      return a.goal?.toLowerCase() > b.goal?.toLowerCase() ? 1 : -1;
    } else {
      return a.teamName?.toLowerCase() > b.teamName?.toLowerCase() ? 1 : -1;
    }
  });
  arr?.forEach((node) => node.goals ? sortTree(node.goals) : '');
}

class OkrAlignmentTable extends React.Component {
  constructor(props) {
    super(props);
    this.recursiveData = [];
    // let recursiveGoals = this.getRecursiveData(this.props.goals || []);
    this.state = {
      originalData: [],
      altOriginalData: [],
      goals: [],
      altGoals: [], //Alt goals used for the goals fetched on mount. switched during filter changes
      filterValue: '1',
      indentGoals: [],
      showSlideshow: false,
      currentSlideDataIndex: 0,
      pagination: true,
      altPagination: true,
      searchParams: {},
      loading: true,
      asyncChangeFilter: null // If waiting for the longer API, wait until it's done.
    };
    this.collapseClick = this.collapseClick.bind(this);
    this.recursiveGoals = this.recursiveGoals.bind(this);
    this.resetRecursiveData = this.resetRecursiveData.bind(this);
    this.showMetrics = this.showMetrics.bind(this);
    this.viewOkrClick = this.viewOkrClick.bind(this);
    this.topViewClick = this.topViewClick.bind(this);
    this.onChangeFilters = this.onChangeFilters.bind(this);
    this.setContainerHeight = this.setContainerHeight.bind(this);
    this.updateGoalData = this.updateGoalData.bind(this);
    this.viewMetricDetails = this.viewMetricDetails.bind(this);
    this.GoalServices = new GoalServices();
    if (this.props.from && this.props.from == "okr-alignment") {
      this.GoalServices.getGoalAlignmentSearchv0 = this.GoalServices.getGoalAlignmentSearch;
      // this.GoalServices.getGoalAlignmentSearch = this.GoalServices.getFullAlignmentReport;
    };
    this.clickCount = 0;
    this.clickTimer = null;
    this.call = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.fetchingMoreData = this.fetchingMoreData.bind(this);
    this.delayCallback = debounce(this.handleScroll, 300);
    this.searchFullAlignData = this.searchFullAlignData.bind(this);
    this.triggerSearch = this.triggerSearch.bind(this);
    this.getGoalsApi = this.getGoalsApi.bind(this);
    this.fetchFullData = this.fetchFullData.bind(this);
  }

  /**
   * Merge alignment filter params (dates, goal status, custom fields, etc.) with request-specific
   * fields (team/user/sid search). Search/autocomplete overrides must win over saved filters.
   */
  buildAlignRequestParams(extra = {}) {
    return {
      userId: this.props.userId ?? 0,
      ...(this.props.filterParams || {}),
      ...extra,
    };
  }

  fetchFullData(params) {
    const api = new GoalServices();
    this.setState({ loading: true });
    return new Promise((resolve, reject) => {
      if (this.call) {
        this.call.cancel("Cancelled API Request");
      }
      this.call = axios.CancelToken.source();
      resolve(api.getGoalAlignmentSearch(params, this.call.token)
      .then(res => {
        console.log(res);
        if (res.data && res.data.goals) {
          const goals = res.data.goals || [];
          const recursiveGoals = this.getRecursiveData(goals);
          this.setState({ 
            loading: false, 
            altOriginalData: goals, 
            altGoals: recursiveGoals,
            altPagination: goals.length === 10
          }, () => {
            if (this.state.asyncChangeFilter) {
              this.onChangeFilters(null, this.state.asyncChangeFilter);
            }
          });
        }
      })
      .catch(err => console.log(err))
      );
    })
  }

  componentDidMount() {
    this.callRequest = false;
    // Build initial params, including any filter params passed from parent
    const initialParams = this.buildAlignRequestParams();
    // Fetch full data to populate the other filter views
    // Takes longer and runs in parallel, non-UI-blocking
    if (this.props.from && this.props.from == "okr-alignment") {
      this.fetchFullData(initialParams);
    }
    // Fetch data to populate default view
    this.searchFullAlignData(initialParams);
    window.addEventListener('scroll', this.delayCallback, false);
  }

  componentDidUpdate(prevProps, prevState) {
    if ((this.props.filterValue != prevProps.filterValue) 
    || (typeof this.props.isPagination !== 'undefined' && typeof prevProps.isPagination !== 'undefined' && this.props.isPagination != prevProps.isPagination )
    || this.props.goals !== prevProps.goals){
      let stateObj = {currentSlideDataIndex: 0, filterValue: this.props.filterValue};
      this.setState(stateObj, () => {
        let recursiveGoals = this.getRecursiveData(this.props.goals || []);
        this.setState({ goals: recursiveGoals, originalData: this.props.goals, indentGoals: [] });
      });
    }

    const searchTriggered = !!this.props.triggerSearch;
    if (searchTriggered) {
      this.triggerSearch(this.props.triggerSearch);
    }

    // Handle filter params change from alignment filter.
    // Skip when a search selection fired in this same update cycle: triggerSearch already
    // issues the scoped (sid) request with saved filters merged in. Running this branch too
    // would fire a filter-only request without sid that wins the in-flight guard (callRequest)
    // and blocks the correct scoped request, leaving the selected objective with no data.
    if (!searchTriggered && this.props.filterParams && this.props.filterParams !== prevProps.filterParams) {
      this.callRequest = false; // Reset to allow the API call
      // Include userId in the params - required by backend to fetch goals
      const params = this.buildAlignRequestParams();
      this.searchFullAlignData(params);
    }
  }

  componentWillUnmount() {
    this.callRequest = false;
    window.removeEventListener('scroll', this.delayCallback, false);
  }

  getRecursiveData(goals = [], i = 0, refId = 0, withOutFilter = false, lazyLoaded = false) {
    this.resetRecursiveData();
    this.recursiveGoals(goals, i, refId, withOutFilter, false, lazyLoaded);
    return this.recursiveData;
  }

  getMaxExpandLevel(goals = []) {
    let g = goals.filter(g => g.collapse).map(g1 => g1.parentRow);
    let maxLevel = g.length ? Math.max(...g) + 1 : 0;
    return maxLevel > 0 ? maxLevel : 0;
  }

  recursiveGoals(goals = [], i = 0, refId = 0, withOutFilter = false, showLevel = false, lazyLoaded = false) {
    let filterValue = typeof this.state !== 'undefined'
      && typeof this.state.filterValue !== 'undefined'
      ? this.state.filterValue
      : "1";
    if (Array.isArray(goals) && goals.length) {
      let goalsLength = goals.length;
      goals.map((g, ind) => {
        let _id = `${refId}_${ind}_${g.id}`;
        let collapse = false;
        let showMetrics = false;
        let showTable = false;
        if (!withOutFilter) {
          showTable = i < 1;
          if (filterValue == '1') {
            if (i < 2) {
              collapse = i < 1;
              showTable = true;
            }
          } else if (filterValue == '2' || filterValue == '3') {
            collapse = true;
            showTable = true;
            showMetrics = filterValue == '3';
          }
          else if (filterValue == '5') { // goalsalign one level up and down in goal details page.
            collapse = !!((goalsLength - 1 == ind && i == 0));
            if ((i == 1 || i == 2) && showLevel) {
              collapse = (g.id == this.props.goalId);
              showTable = true;
            }
          }
          collapse = !!(collapse && g.goalsCount > 0);
        } else {
          showTable = i < 1;
        }
        if (lazyLoaded) {
          showTable = true;
        }
        g = Object.assign(g, {
          parentRow: i,
          rowId: ind,
          parentRefId: _id,
          collapse,
          showMetrics,
          showTable,
        });
        this.recursiveData = this.recursiveData.concat(g);
        if (
          typeof g.goals !== 'undefined'
          && Array.isArray(g.goals)
          && g.goals.length
        ) {
          let _showLevel = !!(((goalsLength - 1 == ind && i == 0) || (i == 1 && showLevel && collapse)));
          return this.recursiveGoals(g.goals, i + 1, _id, withOutFilter, _showLevel, lazyLoaded);
        }
      });
    }
  }

  collapseClick(e, goal) {
    function setCollapseAndMetricsShow(g, goal) {
      if (g.parentRefId == goal.parentRefId) {
        g.collapse = !!(collaspe && g.goalsCount > 0);
        g.showMetrics = false;
      } else if (regExp.test(g.parentRefId)) {
        let pid = `${goal.parentRefId}_${g.rowId}_${g.id}`;
        g.collapse = false;
        g.showTable = false;
        if (collaspe && g.parentRefId == pid) {
          g.showTable = true;
        }
        g.showMetrics = false;
      }
      return g;
    }
    e.preventDefault();
    let collaspe = !goal.collapse;
    let regExp = new RegExp(`^${goal.parentRefId}_`);

    if (goal.goalsCount && (!goal.goals || !goal.goals.length)) {
      showLoaderWidget($("#content"));
      this.getGoalsApi({
        goalId: goal.id,
        nestedGoal: true
      })
      .then(res => {
        // Find where the response should be inserted into the goals array, 
        // as well as include the response in the `goals` field of that goal (goal.goal)
        let customGoals = [];
        let insertIndex;
        let goals = this.state.goals.map((g, i) => {
          if (g.id == goal.id && g.parentRefId == goal.parentRefId) {
            const copyGoal = res.data.goals[0].goals;
            customGoals = this.getRecursiveData(copyGoal, g.parentRow + 1, g.parentRefId, false, true);
            g.goals = customGoals;
            insertIndex = i;
          }
          return setCollapseAndMetricsShow(g, goal);
        });

        // Insert the response into the correct place of the formatted goals
        goals = goals.slice(0, insertIndex + 1).concat(customGoals).concat(goals.slice(insertIndex + 1));
        removeLoaderWidget(true);
        this.setState({ goals });
      })
    } else {
      let goals = this.state.goals.map((g) => {
        return setCollapseAndMetricsShow(g, goal);
      });
      this.setState({ goals });
    }
  }

  topViewClick(e, goal) {
    e.preventDefault();

    let _this = this;
    showLoaderWidget($('#content'));
    let regExp = new RegExp(`^${goal.parentRefId}`);
    this.getGoalsApi({
      goalId: goal.id,
      topViewClick: true,
    }).then((response) => {
      removeLoaderWidget();
      if (response.data) {
        let topViewGoals = response.data.goals || [];
        let topViewGoalsLength = topViewGoals.length;
        let indentGoals = [];
        if (topViewGoalsLength) {
          // Include the existing loaded children inside the goals
          // For the recursive goals build
          for (let i = 0; i < topViewGoals.length; i++) {
            let goals = topViewGoals[i].goals ?? [];
            for (let j = 0; j < goals.length; j++) {
              if (goals[j].id == goal.id) {
                goals[j].goals = cloneDeep(goal.goals);
              }
            }
          }
          let positionId = goal.parentRefId.split('_')[1];
          let recursiveGoals = _this.getRecursiveData(topViewGoals, 0, 0, true);
          if (recursiveGoals) {
            // rebuild parent ref id;
            let regExp1 = '';
            let currentParentRefId = '';
            recursiveGoals = recursiveGoals.map((g) => {
              let postions = g.parentRefId.split('_');
              postions[1] = postions[1] + positionId;
              g.parentRefId = postions.join('_');
              if (g.parentRow == 0) {
                indentGoals.push(g.parentRefId);
              }
              if (g.parentRow == 0 && topViewGoalsLength - 1 == g.rowId) {
                g.collapse = g.goalsCount > 0;
                regExp1 = new RegExp(`^${g.parentRefId}`);
              } else if (regExp1 && regExp1.test(g.parentRefId)) {
                if (g.parentRow == 1) {
                  g.showTable = true;
                  if (g.id == goal.id && g.goalsCount > 0) {
                    g.collapse = true;
                    currentParentRefId = g.parentRefId;
                  }
                } else if (g.parentRow == 2 && currentParentRefId) {
                  let pid = `${currentParentRefId}_${g.rowId}_${g.id}`;
                  if (g.parentRefId == pid) {
                    g.showTable = true;
                  }
                }
              }
              return g;
            });
          }
          let goals = _this.state.goals;
          let findIndex1 = goals.findIndex(
            g => g.parentRefId == goal.parentRefId,
          );
          goals = goals.filter(g => !regExp.test(g.parentRefId));
          let state1 = goals.splice(0, findIndex1);
          state1.splice(findIndex1, 1, ...recursiveGoals);
          if (goals) {
            goals = goals.map((g) => {
              let postions = g.parentRefId.split('_');
              postions[1] = postions[1] + topViewGoalsLength;
              g.parentRefId = postions.join('_');
              return g;
            });
          }
          state1 = state1.concat(...goals);
          _this.setState({ goals: state1, indentGoals }, () => {
            _this.setContainerHeight();
          });
        }
      }
    });
  }

  updateGoalData(obj = {}) {
    let { data = {} } = obj;

    let _this = this;
    if (typeof data === 'object' && Object.keys(data).length) {
      if (typeof data.action !== 'undefined' && data.action == 'delete') {
        if (typeof data.goalId !== 'undefined' && data.goalId > 0) {
          let goalRarentRepIds = this.state.goals
            .filter(g => g.id == data.goalId)
            .map(g => g.parentRefId);

          let goals = this.state.goals.filter((g) => {
            if (g.id != data.goalId) {
              if (goalRarentRepIds.length) {
                for (let s of goalRarentRepIds) {
                  let regExp = new RegExp(`^${s}`);
                  if (regExp.test(g.parentRefId)) {
                    return false;
                  }
                }
              }
              return g;
            }
            return false;
          });
          this.setState({ goals });
        }
      } else {
        if (typeof data.goalId !== 'undefined' && data.goalId > 0) {
          this.getGoalsApi({
            goalId: data.goalId,
            getGData: true,
          }).then((response) => {
            if (response.data) {
              let { goals = [] } = response.data;
              if (goals.length) {
                let goalData = goals[0];
                let _goals = _this.state.goals.map((g) => {
                  if (g.id == data.goalId) {
                    let updateData = {
                      goal: goalData.goal,
                      endDate: goalData.endDate,
                      progressColor: goalData.progressColor,
                      goalPercentage: goalData.goalPercentage,
                      metricsCount: goalData.metricsCount,
                      metrics: goalData.metrics,
                    };

                    g = Object.assign(g, updateData);
                  }
                  return g;
                });
                _this.setState({ goals: _goals });
              }
            }
          });
        }
      }
    }
  }

  setContainerHeight() {
    setTimeout(() => {
      if ($('.goalAlignRender').length && $('.fullalignment-report-wrapper').length) {
        var height = $('.fullalignment-report-wrapper').height();
        $('.goalAlignRender').append(
          `<style> #content {height:${height + 60}px }</style>`,
        );
      }
    }, 100);
  }

  onChangeFilters(e, item) {
    if (this.state.loading) {
      // If full data is still loading, wait for it to finish before unblocking user
      showLoaderWidget($("#content"));
      this.setState({ asyncChangeFilter: item });
      return;
    }
    let showSlideshow = false;
    let filterValue = item.value;
    let goals = this.state.goals;
    let altGoals = this.state.altGoals;
    let originalData = this.state.originalData;
    let altOriginalData = this.state.altOriginalData;
    let pagination = this.state.pagination;
    let altPagination = this.state.altPagination;

    // Since default filter vs other filters are working with different datasets,
    // Swap the datasets if the filter is changed
    if ((filterValue == "1" && this.state.filterValue != "1")
      || (filterValue != "1" && this.state.filterValue == "1")) {
      goals = this.state.altGoals;
      altGoals = this.state.goals;
      originalData = this.state.altOriginalData;
      altOriginalData = this.state.originalData;
      pagination = this.state.altPagination;
      altPagination = this.state.pagination;
    }

    goals = goals.map((g) => {
      let collapse = false;
      let showMetrics = false;
      let showTable = g.parentRow < 1;
      if (filterValue == '1') {
        if (g.parentRow < 2) {
          collapse = g.parentRow < 1;
          showTable = true;
        }
      } else if (filterValue == '2' || filterValue == '3') {
        collapse = true;
        showTable = true;
        showMetrics = !!(filterValue == '3' && g.metricsCount > 0);
      }
      return Object.assign(g, { collapse, showMetrics, showTable });
    });

    $("body").css({ 'overflow': 'visible' });
    // trapping one-level alignment value to preserve existing state behind the slideshow
    if (filterValue === '6') {
      $("body").css({ 'overflow': 'hidden' });
      // set it to the current/previous filterValue present in state
      filterValue = this.state.filterValue;
      // then set showSlideshow value
      showSlideshow = true;
      wb_track('open_one_level_alignment', { email: 'no data' });
      oneLevelAlignmentEventTracker('ONE_LEVEL_ALIGNMENT_VIEWED', 'FULL_ALIGNMENT');
    }
    else {
      let eventParams = {};
      eventParams.eventType = 'ALIGNMENT_FULL_REPORT_VIEWED';
      if (filterValue == '1') {
        eventParams.eventView = 'DEFAULT';
      }
      else if (filterValue == '2') {
        eventParams.eventView = 'ALL_OKRS';
      }
      else if (filterValue == '3') {
        eventParams.eventView = 'ALL_OKRS_AND_KRS';
      }
      else if (filterValue == '4') {
        eventParams.eventView = 'TOP_OKRS';
      }
      this.GoalServices.triggerTrackEvent(eventParams);
    }
    this.setState({ 
      filterValue, 
      goals, 
      originalData, 
      showSlideshow, 
      altGoals, 
      altOriginalData, 
      pagination, 
      altPagination, 
      asyncChangeFilter: null 
    }, () => {
      removeLoaderWidget();
      this.setContainerHeight();
    });
  }

  /**
   * Returns the correct API depending on the current filter value
   * @param params API parameters
   */
  getGoalsApi(params, fullData = false) {
    return (this.state.filterValue != "1" || fullData) ?
      new GoalServices().getGoalAlignmentSearch(params) :
      new GoalServices().getFullAlignmentReport(params);
  }

  getFilterOptions() {
    const { t } = this.props;
    return [
      {
        name: t('Default view'),
        value: '1',
      },
      {
        name: t('All Objectives'),
        value: '2',
      },
      {
        name: t('All Objectives & KRs'),
        value: '3',
      },
      {
        name: t('Top OKRs'),
        value: '4',
      },
      {
        name: t('Two-level alignment'),
        value: '6',
      },
    ];
  }

  handleScroll() {
    const windowHeight = "innerHeight" in window ? window.innerHeight : document.documentElement.offsetHeight;
    const body = document.body;
    const html = document.documentElement;
    const docHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
    const windowBottom = windowHeight + window.pageYOffset;
    if (windowBottom >= (docHeight - 100)) {
      // you're at the bottom of the page
      console.log('handleScroll');
      this.fetchingMoreData();
    }
  }

  fetchingMoreData() {
    let { pagination, searchParams, originalData } = this.state;
    let ele = document.querySelector('#goal-align-page');
    const getMoreData = false;
    if (pagination && ele) {
      let params = { ...searchParams };
      params.paginate = 1;
      params.goalOffset = originalData.length;
      this.searchFullAlignData(params, true);
    }
  } 
  
  getLastSearchParams() {
    return this.state.searchParams || {};
  }

  searchFullAlignData(params = {}, expandText = false, hideLoader = false) {
    let _this = this;
    let ajaxCallTimeOut = null;
    // if (Object.keys(params).length) {
    clearTimeout(ajaxCallTimeOut);
    if (this.callRequest) return;
    this.callRequest = true;
    if (!hideLoader) {
      showLoaderWidget($("#content"));
    }

    return this.getGoalsApi(params).then(function (response) {
      if (!hideLoader) {
        removeLoaderWidget(true);
      }

      if (response.data) {
        let goalsLength = typeof response.data["goals"] !== "undefined" &&
          Array.isArray(response.data.goals) &&
          response.data.goals.length ? response.data.goals.length : 0;
        if (goalsLength > 0) {
          // sort teamName A-Z, name A-Z
          sortTree(response.data.goals);
        }
        let showPublisher = goalsLength ? true : false;
        let { heading, show_confidence_flag, show_real_owner, 
          originalData } = _this.state;
        let goals = [];

        if (typeof params.paginate && params.paginate == 1) {
          showPublisher = true;
          goals = [...originalData, ...response.data.goals];
        } else {
          heading = response.data.heading;
          goals = response.data.goals;
          show_confidence_flag = response.data.show_confidence_flag;
          show_real_owner = response.data.show_real_owner;
        }

        let recursiveGoals = _this.getRecursiveData(goals || []);

        _this.setState(
          {
            searchParams: params,
            pagination: goalsLength === 10,
            goals: recursiveGoals,
            show_real_owner,
            show_confidence_flag,
            heading,
            originalData: goals
          },
          () => {
            _this.props.registerAutocomplete();
            _this.props.setExpandText(expandText);
            _this.props.setShowPublisher(showPublisher);
            _this.setContainerHeight();
          }
        );
      }
      _this.callRequest = false;
      return false;
    }).catch(error => {
      console.log(error);
      if (!hideLoader) {
        removeLoaderWidget(true);
      }
      _this.callRequest = false;
      return;
    });
  }

  triggerSearch(item) {
    let params = {};
    if (item.type === ALIGNMENT_SEARCH_ITEM_TYPE.TEAM) {
      params.teamId = item.id;
    } else if (item.type === ALIGNMENT_SEARCH_ITEM_TYPE.USER) {
      params.userId = item.id;
    } else {
      params.sid = item.type + "-" + item.id;
    }
    if (
      item.type === ALIGNMENT_SEARCH_ITEM_TYPE.GOAL ||
      item.type === ALIGNMENT_SEARCH_ITEM_TYPE.USER ||
      item.type === ALIGNMENT_SEARCH_ITEM_TYPE.TEAM ||
      item.type === ALIGNMENT_SEARCH_ITEM_TYPE.CATEGORY
    ) {
      params.user_search = true;
    }
    params.refsrc = "Full_alignement_report_search_used";
    this.props.setTriggerSearch(null);
    const requestParams = this.buildAlignRequestParams(params);
    // Mutual exclusion: saved alignment filters can still carry teamId or userId;
    // search selection must not leave the other scope in the request (backend uses team when both present).
    if (item.type === ALIGNMENT_SEARCH_ITEM_TYPE.USER) {
      delete requestParams.teamId;
    } else if (item.type === ALIGNMENT_SEARCH_ITEM_TYPE.TEAM) {
      delete requestParams.userId;
    } else {
      delete requestParams.teamId;
      delete requestParams.userId;
    }
    // reset several states
    this.setState({ 
      filterValue: "1",
      pagination: true,
      altPagination: true
    }, () => {
      // Fetch partial and full data for the selected item (include saved alignment filters)
      this.fetchFullData(requestParams);
      return this.searchFullAlignData(requestParams, true)
    })
  }

  showMetrics(e, goal) {
    const setShowMetrics = (g, goal) => {
      if (g.parentRefId == goal.parentRefId) {
        g.showMetrics = typeof g.showMetrics === 'undefined'
          ? true
          : !g.showMetrics;
      }
      return g;
    };

    e.preventDefault();
    $(e.target)
      .closest('.okr-align-column-goal')
      .find('span')
      .trigger('mouseleave');
    this.clickCount++;
    if (this.clickCount == 1) {
      this.clickTimer = setTimeout(() => {
        this.clickCount = 0;
        if (goal.metricsCount) {
          if (!goal.metrics || !goal.metrics.length) {
            showLoaderWidget($("#content"));
            this.GoalServices.getAlignGoalMetrics({goalId: goal.id})
            .then(res => {
              let goals = this.state.goals.map((g) => {
                if (g.id == goal.id) {
                  g.metrics = res.data.goalMetrics;
                }
                return setShowMetrics(g, goal);
              });
              removeLoaderWidget(true);
              this.setState({ goals }, () => {
                this.setContainerHeight();
              });
            })
          } else {
            let goals = this.state.goals.map((g) => {
              return setShowMetrics(g, goal);
            });
            this.setState({ goals }, () => {
              this.setContainerHeight();
            });
          }
        }
      }, 300);
    }
    else {
      clearInterval(this.clickTimer);
      this.clickCount = 0;
      viewGoalDetailsPage(undefined, { id: goal.id });
    }
  }

  viewOkrClick(e, goal) {
    e.preventDefault();
    return viewGoalDetailsPage(undefined, { id: goal.id });
  }

  resetRecursiveData() {
    this.recursiveData = [];
  }

  renderTable(g, maxLevel = 0, indentGoals = []) {
    let parentRow = g.parentRow || 0;
    let tableStyle = {};
    let padLeft = 0;
    if (parentRow > 0) {
      padLeft = (parentRow - 1) * 40;
      if (!indentGoals.length) {
        padLeft = padLeft + 23;
      }
    }
    let indentStyle = {};
    if (Array.isArray(indentGoals) && indentGoals.length) {
      let isIndentGoals = false;
      for (let id of indentGoals) {
        let regExp = new RegExp(`^${  id}`);
        if (regExp.test(g.parentRefId)) {
          isIndentGoals = true;
          break;
        }
      }
      indentStyle.paddingLeft = (!isIndentGoals) ? `${(parseInt(g.parentRow) + 1) * 23}px` : ((g.parentRow > 0) ? '23px' : '0px');
    }
    tableStyle.paddingLeft = `${padLeft}px`;
    let isShowMetrics = g.showMetrics || false;

    let dateStr = '';
    let titleAttr = {};
    if (g.endDate) {
      dateStr = g.endDate.split(',')[0];
      titleAttr.title = g.endDate;
    }
    let title = (g.teamName) ? g.teamNameAndOwner : g.ownerName;
    let name = (g.teamName) ? g.teamName : g.ownerName;

    return (
      <tr key={`okr_table_left_${g.parentRefId}`}>
        <td>
          <table className="okr-align-table-view" cellPadding="0" cellSpacing="0" style={indentStyle}>
            <tbody>
            <tr>
              {
                (g.parentRow == 0)
                  ? (
                    <td width="23" style={{ verticalAlign: 'text-top' }}>
                      <div style={{ width: '23px' }} className="okr-align-column-topview">
                        {g.topView ? (
                          <div
                            className="icon icon-outline-arrow_upward-24px cursor-pointer"
                            onClick={e => this.topViewClick(e, g)}
                          />
                        ) : (
                          ''
                        )}
                      </div>
                    </td>
                  )
                  : ''}
              <td>
                <table
                  cellPadding="0"
                  cellSpacing="0"
                  className="okr-align-table-view  "
                  style={tableStyle}
                >
                  <tbody>
                  <tr>
                    {
                      parentRow > 0
                        ? <td width="40" style={{ verticalAlign: 'text-top' }}><div style={{ width: '40px' }}><div className="icon icon-alignment_L-24px" /></div></td>
                        : ''
                    }
                    <td width="30" style={{ verticalAlign: 'text-top' }}>
                      <div style={{ width: '30px' }}>
                        {g.goalsCount ? (
                          <div
                            className={`icon ${g.collapse ? 'icon-outline-remove_circle_outline-18px cursor-pointer' : 'icon-outline-add_circle_outline-18px cursor-pointer'
                            } collaspse-icon`
                            }
                            onClick={e => this.collapseClick(e, g)}
                          />
                        ) : ''}
                      </div>
                    </td>
                    <td>
                      <table cellPadding="0" cellSpacing="0" className={`okr-align-table-view borderline ${isShowMetrics ? ' highlighted ' : ''}`}>
                        <tbody>
                        <tr>
                          <td width="50" className={(this.props.goalId == g.id ? ' border-left-blue' : '')} style={{ verticalAlign: 'top', paddingTop: '10px' }}>
                            <div className="okr-align-column-percentage" style={{ color: g.progressColor, width: '50px' }}>
                              {g.goalPercentage}
                            </div>
                          </td>
                          <td className="">
                            <div
                              className="okr-align-column-goal cursor-pointer okr-goalname-click"
                              onClick={e => this.showMetrics(e, g)}
                            >
                              <div><span title={g.goal}>{g.goal}</span></div>
                            </div>
                          </td>
                          <td width="150" className={`${isShowMetrics ? ' highlighted ' : ''}`}>
                            <div className="okr-align-column-ownername">
                              <div className="truncateText showtooltiponoff" title={title}>
                                {name}
                              </div>
                            </div>
                          </td>
                          <td width="80" className={`${isShowMetrics ? ' highlighted ' : ''}`}>
                            <div className="okr-align-column-date">
                              <div>
                                <span {...titleAttr}>{dateStr}</span>
                              </div>
                            </div>
                          </td>
                          <td width="50" className={`${isShowMetrics ? ' highlighted ' : ''}`}>
                            <div className="okr-align-column-viewdetails">
                              <div
                                className="icon icon-outline-open_in_new-18px cursor-pointer"
                                onClick={e => this.viewOkrClick(e, g)}
                              />
                            </div>
                          </td>

                        </tr>
                        </tbody>
                      </table>

                      { g.showMetrics && g.metrics && g.metrics.length > 0 && this.renderMetricTable(g)}

                    </td>
                  </tr>
                  </tbody>
                </table>
              </td>
            </tr>
            </tbody>
          </table>
        </td>
      </tr>
    );
  }

  contentHeader() {
    const {t} = this.props
    return (
      <tr key="left_header">
        <td>
          <table cellPadding="0" cellSpacing="0" className="okr-align-table-view okr-align-table-view-header borderline">
            <tbody>
            <tr>
              <td width="3%" />
              <td><table cellPadding="0" cellSpacing="0" className="okr-align-table-view okr-align-table-view-header"><tbody><tr><td></td></tr></tbody></table></td>
              <td width="150"><div className="okr-header-title" style={{ paddingLeft: '10px' }}>{t("Owner")}</div></td>
              <td width="80"><div className="okr-header-title" style={{ paddingLeft: '5px' }}>{t("End Date")} </div></td>
              <td width="50"><div className="okr-header-title">{t("View")} </div></td>
            </tr>
            </tbody>
          </table>
        </td>
      </tr>
    );
  }

  viewMetricDetails = (e, metric) => {
    e.stopPropagation();
		metricDetailsPage(metric.id,null,true);
  }

  renderMetricTable(goal, maxLevel = 0) {
    let metrics = goal.metrics;
    const { show_real_owner } = this.state;
    return (
      <table key={`rl-me-${goal.parentRefId}`} cellPadding="0" cellSpacing="0" className="okr-align-table-view okr-align-table-view-metric">
        <tbody>
        { metrics.map((m, index) => (
          <tr key={`ls-${m.id}`}>
            <td width="60">
              <div style={{ width: '60px' }}>&nbsp;</div>
            </td>
            <td width="150">
              <div className="okr-align-column-met-percentage" style={{ width: '150px' }}>
                <div className="okr-align-met-per-text truncateText">
                    <span
                      className="okr-met-achieve-text"
                      style={{ color: m.progress_color || m.progressColor || 0 }}
                    >
                      {m.achieveTargetText}
                    </span>
                  {m.targetDisplay ? (
                    <span className="okr-met-target-text">
                        {' '}
                      of
                      {' '}
                      {m.totalTargetText}
                      </span>
                  ) : (
                    ''
                  )}
                  { (this.state.show_confidence_flag &&
                      Object.keys(m.confidence).length && m.confidence.confidence != 0) ?
                        <span className="flagFloat">
                          <ConfidenceFlag
                            type={m.confidence.confidence}
                            cDate={m.confidence.created_at_formatted}
                            uName={m.confidence.added_by_name}
                            comment={m.confidence.comment}
                            classProp={'krRow'}
                            style={{'marginBottom': '-2px'}}
                            id={m.id}
                          />
                        </span> : ""
                  }
                </div>
              </div>
            </td>
            <td>
              <div className="okr-align-column-met-name" onClick={(e) => this.viewMetricDetails(e, m)}>
                <div>

                  <span title={m.name}>{m.name}</span>

                </div>
              </div>
            </td>

            <td>
              <div className="sourceTextDiv" title={ show_real_owner ? ( m.source == 5 || m.sourceText !== m.realOwnerName ? `Data from ${m.sourceText}` : '' ) : m.sourceText}> 
                <span className="metric-source-from truncateText" >{m.realOwnerName}</span>
              {
                show_real_owner
                && <KrDataSourceIcon
                  kr={m}
                  isOwnerTheDataSource={m.sourceText === m.realOwnerName}
                  className="author-icon"
                />
              }
              </div>
            </td>

          </tr>
        ))}
        </tbody>
      </table>
    );
  }

  closeAlignmentSlider = () => {
    wb_track('close_one_level_alignment', { email: 'no data' });
    let { goals, altGoals, originalData, altOriginalData, pagination, altPagination } = this.state;
    if (this.state.filterValue == "1") {
      this.setState({
        showSlideshow: false,
        goals: altGoals,
        altGoals: goals,
        originalData: altOriginalData,
        altOriginalData: originalData,
        pagination: altPagination,
        altPagination: pagination,
      });
    } else {
      this.setState({
        showSlideshow: false
      })
    }
    $("body").css({ 'overflow': 'visible'});
  }

  prevArrowClick = () => {
    wb_track('shift_previous_one_level_alignment', { email: 'no data' });
    let newDataIndex = this.state.currentSlideDataIndex - 1;
    if (newDataIndex < 0) {
      newDataIndex = this.state.originalData.length - 1;
    }
    this.setState({
      currentSlideDataIndex: newDataIndex,
    });
  }

  nextArrowClick = () => {
    wb_track('shift_next_one_level_alignment', { email: 'no data' });
    let newDataIndex = this.state.currentSlideDataIndex + 1;
    if (newDataIndex > this.state.originalData.length - 1) {
      newDataIndex = 0;
    }
    this.setState({
      currentSlideDataIndex: newDataIndex,
    });
  }

  showTableContent = () => {
    const { t } = this.props;
    let goals = this.state.goals.filter(g => g.showTable);
    let maxLevel = this.getMaxExpandLevel(goals);

    if (!goals.length) return <ErrorDisplay errorText={t('Search for a person or team to see their OKRalignment.')}/>;

    let indentGoals = this.state.indentGoals || [];
    const allContent = goals.map((g) => this.renderTable(g, maxLevel, indentGoals));
    
    return (
      <>
      { 
      this.props.showTitle ?
        <div className="width100">
          <div className="right">
            <DropdownComp
              defaultValue={this.state.filterValue}
              options={this.getFilterOptions()}
              onChange={this.onChangeFilters}
              style = {true}
              appendId="okr-align-table"
            />
          </div>
          <div className="clear" />
        </div>
        : '' 
      }
      <div className={`width100 fullalignment-report-wrapper left ${this.props.wrapperClass}` } style={{ overflowX: 'scroll'}}>
        {
        this.state.originalData && this.state.showSlideshow
          ? (
            <div style={{ minHeight: '0px', minWidth: '0px' }}>
              <AlignmentSlider
                slideData={this.state.originalData}
                currentSlideDataIndex={this.state.currentSlideDataIndex}
                closeSlider={this.closeAlignmentSlider.bind(this)}
                prevArrowClick={this.prevArrowClick}
                nextArrowClick={this.nextArrowClick}
              />
            </div>
          ) : ''
      }
        {
        this.props.showTitle
          ? (
            <div style={{ padding: '20px 20px 0px 10px' }}>
              <UserInfoComp {...this.state.heading} />
            </div>
          )
          : ''
      }
        <div className="clear" />
        {
          this.state.originalData ? 
            <div style={{ margin: '0px 20px' }}>
              <div className="width100 okralignment-table">
                <div className="okralignment-table">
                  <table cellPadding="0" cellSpacing="0" className="okr-align-table-view">
                    <tbody>
                      { this.props.showHeader && this.contentHeader()}
                      {allContent}
                    </tbody>
                  </table>
                  <div style={{ height: '10px' }} />
                </div>
                <div className="clear" />
              </div>
            </div> 
            : <ErrorDisplay errorText={t('Search for a person or team to see their OKR alignment.')}/>
        }
        <div className="clear" />
      </div>
      </>
    );
  }

  render() {
    return this.showTableContent();
  }
}

OkrAlignmentTable.defaultProps = {
  // goals: [],
  showHeader: true,
  showTitle: true,
  filterValue: '1',
  goalId: null,
  wrapperClass: '',
  setExpandText: ()=>{},
  setShowPublisher: ()=>{},
};

const UserInfoComp = ({ pic = '', title = '' } = props) => (
  <div className="userinfocomp-wrapper">
    <div className="width100">
      {pic ? (
        <div className="userinfocomp-pic">
          <img src={pic} className="fitProfileImage" />
        </div>
      ) : (
        ''
      )}
      <div className="userinfocomp-name">{title}</div>
    </div>
  </div>
);

export default withTranslation()(OkrAlignmentTable);
